import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const FeaturedCaseStudies = () => {
  const caseStudies = [
    {
      id: 1,
      title: "AI-Powered Fraud Detection System",
      client: "Global Financial Services",
      industry: "FinTech",
      description: "Implemented a machine learning solution that reduced fraudulent transactions by 85% while maintaining seamless user experience.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
      clientLogo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=30&fit=crop",
      results: [
        { metric: "85%", label: "Fraud Reduction" },
        { metric: "0.2s", label: "Response Time" },
        { metric: "$2.4M", label: "Annual Savings" }
      ],
      technologies: ["Python", "TensorFlow", "AWS", "Real-time Analytics"],
      duration: "8 months",
      category: "AI Models"
    },
    {
      id: 2,
      title: "Enterprise Cybersecurity Platform",
      client: "Healthcare Network",
      industry: "Healthcare",
      description: "Developed a comprehensive security solution protecting patient data across 50+ medical facilities with zero security breaches.",
      image: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600&h=400&fit=crop",
      clientLogo: "https://images.unsplash.com/photo-1549923746-c502d488b3ea?w=100&h=30&fit=crop",
      results: [
        { metric: "100%", label: "Uptime" },
        { metric: "0", label: "Security Breaches" },
        { metric: "50+", label: "Facilities Protected" }
      ],
      technologies: ["Node.js", "React", "MongoDB", "Encryption"],
      duration: "12 months",
      category: "Cybersecurity"
    },
    {
      id: 3,
      title: "Predictive Analytics Dashboard",
      client: "Manufacturing Giant",
      industry: "Manufacturing",
      description: "Built an intelligent analytics platform that predicts equipment failures 30 days in advance, reducing downtime by 60%.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
      clientLogo: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=100&h=30&fit=crop",
      results: [
        { metric: "60%", label: "Downtime Reduction" },
        { metric: "30 days", label: "Prediction Window" },
        { metric: "$5.2M", label: "Cost Savings" }
      ],
      technologies: ["React", "D3.js", "Python", "IoT Integration"],
      duration: "10 months",
      category: "Analytics"
    }
  ];

  const getCategoryColor = (category) => {
    const colorMap = {
      "AI Models": "bg-secondary/10 text-secondary",
      "Cybersecurity": "bg-warning/10 text-warning",
      "Analytics": "bg-accent/10 text-accent",
      "Software Development": "bg-primary/10 text-primary",
      "AI Engineering": "bg-success/10 text-success"
    };
    return colorMap[category] || "bg-primary/10 text-primary";
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Trophy" size={16} />
            <span>Success Stories</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Featured Case Studies
          </h2>
          
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Discover how we've helped leading organizations achieve breakthrough results through innovative AI and technology solutions.
          </p>
        </div>

        {/* Case Studies Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {caseStudies.map((study) => (
            <Link
              key={study.id}
              to="/individual-service-detail"
              className="group bg-white rounded-xl border border-border hover:border-primary/30 shadow-soft hover:shadow-elevation transition-all duration-300 overflow-hidden hover:-translate-y-1"
            >
              {/* Image */}
              <div className="relative overflow-hidden">
                <Image
                  src={study.image}
                  alt={study.title}
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(study.category)}`}>
                    {study.category}
                  </span>
                </div>
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-2">
                  <Image
                    src={study.clientLogo}
                    alt={`${study.client} logo`}
                    className="h-6 object-contain"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-3">
                  <Icon name="Building2" size={14} />
                  <span>{study.industry}</span>
                  <span>•</span>
                  <Icon name="Clock" size={14} />
                  <span>{study.duration}</span>
                </div>

                <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                  {study.title}
                </h3>

                <p className="text-muted-foreground mb-6 leading-relaxed">
                  {study.description}
                </p>

                {/* Results */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  {study.results.map((result, index) => (
                    <div key={index} className="text-center">
                      <div className="text-lg font-bold text-primary mb-1">
                        {result.metric}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {result.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Technologies */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {study.technologies.slice(0, 3).map((tech, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md"
                    >
                      {tech}
                    </span>
                  ))}
                  {study.technologies.length > 3 && (
                    <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md">
                      +{study.technologies.length - 3} more
                    </span>
                  )}
                </div>

                {/* CTA */}
                <div className="flex items-center space-x-2 text-primary font-medium group-hover:space-x-3 transition-all">
                  <span>View Case Study</span>
                  <Icon name="ArrowRight" size={16} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-xl p-8 border border-primary/10">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Ready to Create Your Success Story?
            </h3>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Join the ranks of industry leaders who have transformed their businesses with our AI and technology solutions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact-consultation-request"
                className="inline-flex items-center space-x-2 bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-secondary transition-colors shadow-soft hover:shadow-elevation"
              >
                <Icon name="Calendar" size={18} />
                <span>Schedule Consultation</span>
              </Link>
              <Link
                to="/services-overview"
                className="inline-flex items-center space-x-2 border border-primary text-primary px-6 py-3 rounded-lg font-semibold hover:bg-primary hover:text-white transition-colors"
              >
                <Icon name="FileText" size={18} />
                <span>View All Case Studies</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedCaseStudies;